import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInCalendarWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import type { WallpaperConfig } from '../config/schema';

export type LifeUnit = WallpaperConfig['life']['unit'];

export function lifeGridDimensions(unit: LifeUnit, lifeExpectancy: number): {
  rows: number;
  cols: number;
} {
  switch (unit) {
    case 'weeks':
      return { rows: lifeExpectancy, cols: 52 };
    case 'months':
      return { rows: lifeExpectancy, cols: 12 };
    case 'days':
      return { rows: lifeExpectancy, cols: 365 };
  }
}

export function unitsLived(birthDate: Date, today: Date, unit: LifeUnit): number {
  const lived = (() => {
    switch (unit) {
      case 'days':
        return differenceInCalendarDays(today, birthDate);
      case 'weeks':
        return differenceInCalendarWeeks(today, birthDate, { weekStartsOn: 1 });
      case 'months':
        return differenceInCalendarMonths(today, birthDate);
    }
  })();
  return Math.max(0, lived);
}

export type CellState = 'past' | 'today' | 'future';

export function lifeCellState(
  index: number,
  lived: number,
  highlightToday: boolean,
): CellState {
  if (index < lived) return 'past';
  if (index === lived && highlightToday) return 'today';
  return 'future';
}

export function yearGrid(year: number, weekStartsOn: 0 | 1): {
  cells: { date: Date; weekIndex: number; dayIndex: number }[];
  weeks: number;
} {
  const start = startOfYear(new Date(year, 0, 1));
  const end = endOfYear(start);
  const days = eachDayOfInterval({ start, end });
  const firstWeekday = (start.getDay() - weekStartsOn + 7) % 7;
  const cells = days.map((date, i) => {
    const offset = i + firstWeekday;
    return {
      date,
      weekIndex: Math.floor(offset / 7),
      dayIndex: offset % 7,
    };
  });
  const weeks = cells.length ? cells[cells.length - 1].weekIndex + 1 : 0;
  return { cells, weeks };
}

export function monthGrid(year: number, month: number, weekStartsOn: 0 | 1): {
  cells: { date: Date; inMonth: boolean; weekIndex: number; dayIndex: number }[];
  rows: number;
} {
  const monthStart = startOfMonth(new Date(year, month - 1, 1));
  const monthEnd = endOfMonth(monthStart);
  const startOffset = (monthStart.getDay() - weekStartsOn + 7) % 7;
  const endOffset = (6 - ((monthEnd.getDay() - weekStartsOn + 7) % 7));
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - startOffset);
  const gridEnd = new Date(monthEnd);
  gridEnd.setDate(monthEnd.getDate() + endOffset);
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const cells = days.map((date, i) => ({
    date,
    inMonth: date.getMonth() === month - 1 && date.getFullYear() === year,
    weekIndex: Math.floor(i / 7),
    dayIndex: i % 7,
  }));
  return { cells, rows: cells.length / 7 };
}

export function isSameDay(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

export function parseISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function todayFromOverride(override: string | null): Date {
  if (!override) return new Date();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(override)) return new Date();
  return parseISODate(override);
}

export const WEEKDAY_LETTERS_MON = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
export const WEEKDAY_LETTERS_SUN = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
export const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
